import { useCallback, useEffect, useState } from 'react'
import type { StepBounds } from '../client-config'

// dev 實驗參數（#1727）：一輪最多幾步、同輪工具結果壓縮。本機記的是「上次送出時用的」，
// 由送出那一刻寫入（見 ChatPage），調整本身不寫——與模型選擇同一條規則。
// 🔴 本機儲存只是個人便利：讀寫失敗一律當沒有，聊天照常。

const STORAGE_KEY = 'ai-chat:experiment'

export interface ExperimentState {
  maxSteps: number
  compressToolResults: boolean
  // 思考預算（#1739）。伺服器沒給上下界時缺席
  thinkingBudget?: number
}

/** 送給伺服器的那一份：只帶與預設不同的項目；全是預設就沒有（＝沒設定）。 */
export interface ExperimentRequest {
  maxSteps?: number
  compressToolResults?: boolean
  thinkingBudget?: number
}

export const readStoredExperiment = (): Partial<ExperimentState> | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return typeof parsed === 'object' && parsed !== null ? parsed as Partial<ExperimentState> : null
  } catch {
    return null
  }
}

export const storeExperiment = (state: ExperimentState): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // 寫不進去就只在這次頁面有效，不影響送出
  }
}

const isInBounds = (value: unknown, bounds: StepBounds): value is number =>
  Number.isInteger(value) && (value as number) >= bounds.min && (value as number) <= bounds.max

// 本機記住的值仍在上下界內就沿用，否則回到預設（上下界改過、舊值、壞值都一樣處理）
export const pickInitialExperiment = (
  stored: Partial<ExperimentState> | null, bounds: StepBounds, thinkingBounds?: StepBounds | null,
): ExperimentState => ({
  maxSteps: isInBounds(stored?.maxSteps, bounds) ? stored?.maxSteps as number : bounds.default,
  compressToolResults: stored?.compressToolResults === true,
  ...(thinkingBounds ? {
    thinkingBudget: isInBounds(stored?.thinkingBudget, thinkingBounds) ?
      stored?.thinkingBudget as number : thinkingBounds.default,
  } : {}),
})

/** 只帶與預設不同的項目。都沒改就回 null，請求不帶 experiment。 */
export const toExperimentRequest = (
  state: ExperimentState, bounds: StepBounds, thinkingBounds?: StepBounds | null,
): ExperimentRequest | null => {
  const request: ExperimentRequest = {
    ...(state.maxSteps !== bounds.default ? { maxSteps: state.maxSteps } : {}),
    ...(state.compressToolResults ? { compressToolResults: true } : {}),
    ...(thinkingBounds && state.thinkingBudget !== undefined &&
      state.thinkingBudget !== thinkingBounds.default ? { thinkingBudget: state.thinkingBudget } : {}),
  }
  return Object.keys(request).length ? request : null
}

// 輸入框的字轉成步數：不是整數就不收，超出範圍夾回上下界
export const clampSteps = (text: string, bounds: StepBounds): number | null => {
  const value = Number(text)
  if (text.trim() === '' || !Number.isInteger(value)) return null
  return Math.min(bounds.max, Math.max(bounds.min, value))
}

// 思考預算的選項（#1739）：上下界內的 2 的次方，加上預設與上界。
// 用選單而非輸入框：下界 512，逐字輸入時前幾個字一定低於下界
export const thinkingBudgetOptions = (bounds: StepBounds): number[] => {
  const options = new Set([bounds.default, bounds.max])
  for (let value = 1; value <= bounds.max; value *= 2) {
    if (value >= bounds.min) options.add(value)
  }
  return [...options].sort((a, b) => a - b)
}

// 沒有上下界（非 dev 或設定還沒讀回）時 state 是 null，送出時就不帶實驗參數
export const useExperimentChoice = (
  bounds: StepBounds | null, thinkingBounds: StepBounds | null = null,
) => {
  const [state, setState] = useState<ExperimentState | null>(null)
  useEffect(() => {
    setState(bounds ? pickInitialExperiment(readStoredExperiment(), bounds, thinkingBounds) : null)
  }, [bounds, thinkingBounds])
  const update = useCallback((patch: Partial<ExperimentState>) => {
    setState((current) => (current ? { ...current, ...patch } : current))
  }, [])
  return { state, update }
}
